import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    OnChanges,
    SimpleChanges,
    inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { ConfirmationService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ContactSelectorComponent } from './contact-selector.component';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface ContactWithType {
    contactId: string;
    contactType: string;
    fullName?: string;
    email?: string;
    phone?: string;
    positionTitle?: string;
}

@Component({
    selector: 'qc-multi-contact-manager',
    standalone: true,
    imports: [CommonModule, FormsModule, ButtonModule, ConfirmDialogModule, ContactSelectorComponent],
    template: `
        <div class="space-y-4">
            <div class="flex items-center justify-between">
                <label class="block text-sm font-medium">
                    {{ label }}
                    <span *ngIf="required" class="text-red-500 ml-1">*</span>
                </label>
                <button
                    type="button"
                    pButton
                    icon="pi pi-plus"
                    label="Add Contact"
                    class="p-button-sm p-button-outlined"
                    [disabled]="disabled"
                    (click)="addContact()"
                ></button>
            </div>

            <div
                *ngIf="contacts.length === 0"
                class="text-sm text-gray-500 italic border-2 border-dashed border-gray-200 rounded-lg p-4 text-center"
            >
                No contacts added yet. Click "Add Contact" to get started.
            </div>

            <div *ngFor="let contact of contacts; let i = index" class="border border-gray-200 rounded-lg p-4 bg-white">
                <!-- Contact Selector (hidden when contact is selected or created) -->
                <div *ngIf="!contact.contactId && !contact.fullName" class="mb-3">
                    <qc-contact-selector
                        [label]="'Select Contact'"
                        [required]="false"
                        [showCreateButton]="true"
                        [deferSave]="true"
                        [disabled]="disabled"
                        [initialValue]="null"
                        (contactSelected)="onContactSelected(i, $event)"
                        (contactCreated)="onContactCreated(i, $event)"
                        (contactCleared)="onContactCleared(i)"
                    />
                </div>

                <!-- Contact Information Display (shown when contact is selected or created) -->
                <div *ngIf="contact.contactId || contact.fullName" class="space-y-2">
                    <div class="flex items-start justify-between">
                        <div class="flex-1">
                            <h4 class="font-medium text-gray-900">
                                {{ contact.fullName || 'Unknown Contact' }}
                                <span *ngIf="!contact.contactId" class="ml-2 text-xs text-blue-600 font-normal">(New)</span>
                            </h4>
                            <p *ngIf="contact.positionTitle" class="text-sm text-gray-600">
                                {{ contact.positionTitle }}
                            </p>
                            <p *ngIf="contact.email" class="text-sm text-gray-600">{{ contact.email }}</p>
                            <p *ngIf="contact.phone" class="text-sm text-gray-600">{{ contact.phone }}</p>
                        </div>
                        <button
                            type="button"
                            pButton
                            icon="pi pi-trash"
                            class="p-button-sm p-button-text p-button-danger"
                            [disabled]="disabled"
                            [title]="'Remove contact'"
                            (click)="confirmRemoveContact(i)"
                        ></button>
                    </div>
                </div>
            </div>
        </div>

    `,
})
export class MultiContactManagerComponent implements OnInit, OnDestroy, OnChanges {
    @Input() label = 'Contacts';
    @Input() contactType = '';
    @Input() required = false;
    @Input() disabled = false;
    @Input() initialContacts: ContactWithType[] = [];

    @Output() contactsChange = new EventEmitter<ContactWithType[]>();
    @Output() validationChange = new EventEmitter<boolean>();

    contacts: ContactWithType[] = [];
    private destroy$ = new Subject<void>();
    private confirmationService = inject(ConfirmationService);

    ngOnInit(): void {
        if (this.initialContacts && this.initialContacts.length > 0) {
            this.contacts = [...this.initialContacts];
        }
        this.emitValidation();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['initialContacts']) {
            this.initialContacts = changes['initialContacts'].currentValue;
            if (this.initialContacts && this.initialContacts.length > 0) {
                this.contacts = [...this.initialContacts];
            }
            this.emitValidation();
        }
    }

    addContact(): void {
        this.contacts.push({
            contactId: '',
            contactType: this.contactType,
        });
        // Don't emit immediately - wait for user to select a contact
    }

    removeContact(index: number): void {
        this.contacts.splice(index, 1);
        this.emitChanges();
    }

    onContactSelected(index: number, contact: any): void {
        this.contacts[index] = {
            contactId: contact.id,
            contactType: this.contactType,
            fullName: contact.fullName || '',
            email: contact.email || '',
            phone: contact.phone || '',
            positionTitle: contact.positionTitle || '',
        };
        this.emitChanges();
    }

    onContactCreated(index: number, contact: any): void {
        this.contacts[index] = {
            contactId: '', 
            contactType: this.contactType,
            fullName: contact.fullName || '',
            email: contact.email || '',
            phone: contact.phone || '',
            positionTitle: contact.positionTitle || '',
        };
        this.emitChanges();
    }

    onContactCleared(index: number): void {
        this.contacts[index] = {
            contactId: '',
            contactType: this.contactType,
        };
        this.emitChanges();
    }

    getContactForSelector(contact: ContactWithType): any {
        if (contact.contactId) {
            return {
                id: contact.contactId,
                fullName: contact.fullName || '',
            };
        }
        return null;
    }

    private emitChanges(): void {
    
        const validContacts = this.contacts.filter((contact) => {
            if (contact.contactId && contact.contactId.trim() !== '') {
                return true;
            }
            return contact.fullName && contact.fullName.trim() !== '';
        });
        this.contactsChange.emit(validContacts);
        this.emitValidation();
    }

    private emitValidation(): void {
        const validContacts = this.contacts.filter((contact) => {
            return (contact.contactId && contact.contactId.trim() !== '') ||
                (contact.fullName && contact.fullName.trim() !== '');
        });
        const isValid = this.required ? validContacts.length > 0 : true; // If not required, always valid
        this.validationChange.emit(isValid);
    }

    confirmRemoveContact(index: number): void {
        const contact = this.contacts[index];
        const contactName = contact.fullName || 'this contact';

        this.confirmationService.confirm({
            message: `Are you sure you want to remove ${contactName}?`,
            header: 'Confirm Removal',
            icon: 'pi pi-exclamation-triangle',
            acceptButtonStyleClass: 'p-button-danger',
            accept: () => {
                this.removeContact(index);
            },
        });
    }
}
